import Grid from '@mui/material/Grid';
import { styled } from '@mui/material/styles';
import type React from 'react';
import { useContext } from 'react';
import { ScrollMenu, VisibilityContext } from 'react-horizontal-scrolling-menu';
import 'react-horizontal-scrolling-menu/dist/styles.css';
import { type MenuItemHighlight } from '../types';
import MenuItemCard from './MenuItemCard';
import ScrollingMenuHeader from '@Components/ui/ScrollingMenuHeader';
import '@Components/ui/ScrollingMenuHeader/hideScrollBar.css';
import { StyledSection } from '../../Common/StyledSection';
import { useDesignSchema } from '@Src/providers/DesignSchemaProvider';
import { useTranslation } from '@Src/providers/TranslationProvider';

const StyledContainer = styled(StyledSection)(({ theme }) => ({
  minHeight: '355px',
  [theme.breakpoints.up('sm')]: {
    paddingTop: `${theme.spacing(6)} !important`,
    paddingRight: '0 !important',
    paddingBottom: `${theme.spacing(6)} !important`,
    paddingLeft: '12em !important',
  },
  [theme.breakpoints.down('sm')]: {
    paddingTop: theme.spacing(6),
    paddingRight: 0,
    paddingLeft: theme.spacing(3),
  },
}));

const StyledMenuCardWrapper = styled(Grid)`
  margin-right: ${({ theme }) => theme.spacing(3)};
  height: 100%;
`;

type MenuSliderProps = {
  menuItems: MenuItemHighlight[];
  overrideClickAction?: () => void;
  url: string;
};

const MenuSlider: React.FC<MenuSliderProps> = ({
  menuItems,
  overrideClickAction,
  url,
}): JSX.Element => {
  const { menuHighlights } = useDesignSchema('v2');
  const { translate } = useTranslation();
  const mItems = [...menuItems, {}] as MenuItemHighlight[];

  return (
    <StyledContainer>
      {menuItems.length > 0 && (
        <ScrollMenu
          Header={() => {
            const {
              scrollPrev,
              scrollNext,
              isLastItemVisible,
              isFirstItemVisible,
            } = useContext(VisibilityContext);
            return (
              <ScrollingMenuHeader
                title={translate(menuHighlights?.title || '')}
                subTitle={translate(menuHighlights?.subTitle || '')}
                leftButtonDisabled={isFirstItemVisible}
                rightButtonDisabled={isLastItemVisible}
                goBack={() => {
                  scrollPrev('smooth', 'nearest');
                }}
                goForward={() => {
                  scrollNext('smooth', 'nearest');
                }}
              />
            );
          }}
        >
          {mItems.map((item, idx) => (
            <StyledMenuCardWrapper key={idx}>
              <MenuItemCard
                {...item}
                overrideClickAction={overrideClickAction}
                to={`${
                  url.endsWith('order') ? url : `${url}?item=${item.itemId}`
                }`}
                showViewAllCard={idx === mItems.length - 1}
                storeUrl={url}
                showImage={menuHighlights?.showImages}
              />
            </StyledMenuCardWrapper>
          ))}
        </ScrollMenu>
      )}
    </StyledContainer>
  );
};

export default MenuSlider;
